/**
 * UnitNumberingService
 * 
 * Handles the assignment and management of unit numbers in the diagram.
 * Operates similarly to stream numbering, ensuring unique numbers for units,
 * and provides methods to integrate with the main KitchenSinkService.
 */
class UnitNumberingService {
    /**
     * Creates a new UnitNumberingService
     * 
     * @param {Object} graph - The JointJS graph where units are managed
     */
    constructor(graph) {
      this.graph = graph;
      this.unitCount = 1;

    }
  
    /**
    /**
     * Verifies that a unit number is unique among existing units
     * 
     * @param {number} unitNumber - The unit number to verify
     * @param {string} unitId - The ID of the unit to exclude from check
     * @returns {boolean} - True if number is unique, false otherwise
     */
    unitNumberVerification(unitNumber, unitId) {
      let result = true;
      const existingElements = this.graph.getElements();
      
      for (let i = 0; i < existingElements.length; i++) {
        const element = existingElements[i];
        if (element.prop('unitNumber') === unitNumber && element.id !== unitId) {
          result = false;
          break;
        }
      }
      
      return result;
    }
  
    /**
   * Gets the next available unit number by finding the lowest unused number
   * 
   * @returns {number} - The next available unit number
   */
    getNextUnitNumber() {
      console.log(`Finding next available unit number`);
      
      // Get all existing unit numbers
      const existingUnits = this.graph.getElements()
        .filter(element => element.prop('unitNumber') !== undefined)
        .map(element => element.prop('unitNumber'));
      
      console.log(`Existing unit numbers: ${existingUnits.join(', ')}`);
      
      // Start checking from 1
      let candidate = 1;
      
      // Find the lowest number that isn't being used
      while (existingUnits.includes(candidate)) {
        candidate++;
      }
      
      // Update the counter to max+1 to ensure we don't reuse existing numbers
      const maxExisting = Math.max(...existingUnits, 0);
      this.unitCount = Math.max(candidate + 1, maxExisting + 1);
      
      console.log(`Lowest available unit number: ${candidate}, counter set to: ${this.unitCount}`);
      return candidate;
    }
  
    /**
     * Assigns a unit number to an element
     * 
     * @param {Object} element - The JointJS element to assign a number to
     * @returns {number} - The assigned unit number
     */
    assignUnitNumber(element) {
      // Skip if element already has a unit number
      if (element.prop('unitNumber')) {
        console.log(`Unit already has number: ${element.prop('unitNumber')}`, element.id);
        return element.prop('unitNumber');
      }
      
      // Get next available number and assign it
      const unitNumber = this.getNextUnitNumber();
      element.prop('unitNumber', unitNumber);
      
      console.log(`Assigned unit number: ${unitNumber} to element`, element.id);
      return unitNumber;
    }
  
    /**
     * Handles unit deletion by ensuring proper numbering
     * No explicit action needed here, as the getNextUnitNumber method
     * will automatically find the next available number
     * 
     * @param {Object} element - The element being deleted
     */
    handleUnitDeletion(element) {
      // Check if element has a unit number
      if (element.prop('unitNumber')) {
        const deletedNumber = element.prop('unitNumber');
        console.log(`Unit numbering: Unit ${deletedNumber} has been deleted`);
        
        // If the deleted number was the highest number,
        // we could potentially reduce the counter
        if (deletedNumber === this.unitCount - 1) {
          this.unitCount = deletedNumber;
          console.log(`Unit numbering: Reduced counter to ${this.unitCount}`);
        }
        
        // No need to do anything else since getNextUnitNumber
        // will automatically find the next available number
        // including any "holes" left by deletions
      }
    }
  
    /**
     * Initializes unit numbers for existing elements in the graph
     * Useful when loading an existing diagram that lacks unit numbers
     */
    initializeExistingUnits() {
      console.log('Unit numbering: Initializing existing units');
      const elements = this.graph.getElements();
      
      // Find highest existing unit number
      let highestUnitNumber = 0;
      elements.forEach(element => {
        if (element.prop('unitNumber') && element.prop('unitNumber') > highestUnitNumber) {
          highestUnitNumber = element.prop('unitNumber');
        }
      });
      
      // Set counter to highest number + 1
      this.unitCount = highestUnitNumber + 1;
      console.log(`Unit numbering: Setting unit count to ${this.unitCount} based on highest existing number`);
      
      // Only process units that don't already have a number
      let processedCount = 0;
      elements.forEach(element => {
        // Skip foreignObject elements
        if (element.attributes && element.attributes.type === 'standard.ForeignObject') {
          return;
        }
        
        // Skip elements that already have a unit number
        if (element.prop('unitNumber')) {
          console.log(`Unit numbering: Skipping element that already has number ${element.prop('unitNumber')}`, element.id);
          return;
        }
        
        if (!element.isLink() && 
            element.prop('isUnit') === 'yes' && 
            element.prop('unitType') && 
            element.prop('unitType') !== 'Feed' && 
            element.prop('unitType') !== 'Product' && 
            element.prop('unitType') !== 'pipework') {
          this.assignUnitNumber(element);
          processedCount++;
        }
      });
      
      console.log(`Unit numbering: Assigned numbers to ${processedCount} units during initialization`);
    }
  
    /**
     * Gets all unit numbers currently in use
     * Useful for debugging or UI that might need to show all units
     * 
     * @returns {Array} - Array of objects containing unit IDs and their numbers
     */
    getAllUnitNumbers() {
      const elements = this.graph.getElements();
      const unitNumbers = [];
      
      elements.forEach(element => {
        if (element.prop('unitNumber')) {
          unitNumbers.push({
            id: element.id,
            unitNumber: element.prop('unitNumber'),
            unitType: element.prop('unitType')
          });
        }
      });
      
      return unitNumbers;
    }
    
    /**
     * Reset all unit numbering
     * Useful when clearing a diagram or starting fresh
     */
    resetUnitNumbering() {
      this.unitCount = 1;
      const elements = this.graph.getElements();
      
      elements.forEach(element => {
        if (element.prop('unitNumber')) {
          element.removeProp('unitNumber');
        }
      });
    }
  }
  
  export default UnitNumberingService;